import React, { FC, useCallback } from "react"

type KeyModifiers = {
    shiftKey: boolean
    altKey: boolean
    ctrlKey: boolean
}

export type DivBtnProps = {
    dataTest?: string
    domId?: string
    onClick?: (modifiers: KeyModifiers) => void
    clickThrough?: boolean
    fillWidth?: boolean
    fillHeight?: boolean
    disableTab?: boolean
}

/**
 * Creates a clickable div, with all the required attributes.
 *
 * This component has no styling other than being a flex-box and being
 * able to grow. The styling and dimensions of the button you're
 * creating should be on the child-element.
 */
export const DivBtn: FC<DivBtnProps> = (props) => {

    const { onClick } = props

    const handleOnClick = useCallback(
        (modifiers: KeyModifiers) => onClick && onClick(modifiers),
        [onClick]
    )

    let buttonProps = {}

    if (props.clickThrough == null || props.clickThrough === false) {

        buttonProps = {
            role: "button",
            tabIndex: props.disableTab ? -1 : 0,
            onClick: (e: React.MouseEvent<HTMLDivElement>) => {
                handleOnClick({
                    shiftKey: e.shiftKey,
                    altKey: e.altKey,
                    ctrlKey: e.ctrlKey,
                })
            },
            onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
                // Count enter or space as a click
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    e.stopPropagation()

                    handleOnClick({
                        shiftKey: e.shiftKey,
                        altKey: e.altKey,
                        ctrlKey: e.ctrlKey,
                    })
                }
            }
        }
    }

    return (
        <div
            className="user-select-none"
            id={props.domId}
            data-test={props.dataTest}

            style={{
                display: "flex",
                flexGrow: (props.fillWidth != null) ? 1 : 0,
                alignSelf: (props.fillHeight != null) ? 'stretch' : undefined,
            }}

            {...buttonProps}
        >
            {props.children}
        </div>
    )
}
